import { action, observable, computed, makeAutoObservable } from "mobx";
import { Prices, PricesList, Rates } from "../models/dtoTypes";
import { RootStore } from "./rootStore";


export default class BundleStore {
    _qnt: number = 1;
    priceList: PricesList = new Prices();
    // GBP per seat per MONTH; only set when the managed monthly Price is configured
    monthlyPriceList: PricesList = new Prices();
    // which price the bundle currently quotes: annual (default) or monthly billing
    billingPeriod: 'annual' | 'monthly' = 'annual';
    rates: Rates = {};

    rootStore: RootStore | undefined;

    constructor(rootStore: RootStore, qnt: number) {
        this.rootStore = rootStore;
        this.setQnt(qnt);
        this.increaseQnt = this.increaseQnt.bind(this);
        this.decreaseQnt = this.decreaseQnt.bind(this);
        makeAutoObservable(this);
    }

    @action setPricesAndRates(priceList: PricesList, rates: Rates, monthlyPriceList?: PricesList) {
        this.priceList = priceList;
        this.rates = rates;
        if (monthlyPriceList) this.monthlyPriceList = monthlyPriceList;
    }

    @action setBillingPeriod(period: 'annual' | 'monthly') {
        //monthly can only be quoted when the monthly matrix actually arrived
        this.billingPeriod = period === 'monthly' && this.HasMonthlyPrices ? 'monthly' : 'annual';
    }

    @computed get HasMonthlyPrices(): boolean {
        return Object.keys(this.monthlyPriceList).length > 0;
    }

    @computed get IsMonthly(): boolean {
        return this.billingPeriod === 'monthly';
    }

    //the matrix the current billing period charges from
    @computed get EffectivePriceList(): PricesList {
        return this.IsMonthly ? this.monthlyPriceList : this.priceList;
    }

    //the recurring charge for the whole bundle: per YEAR on annual, per MONTH on monthly
    @computed get BundlePrice(): number {
        const list = this.EffectivePriceList;
        const priceListLength = Object.keys(list).length;
        if (this._qnt <= priceListLength) {
            return list[this._qnt] * this._qnt;
        }
        else {
            return list[priceListLength] * this._qnt
        }

    }
    //computed property for the price based on the quantity of the bundle as string with 2 decimal places
    @computed get BundlePriceString(): string { return this.BundlePrice.toFixed(2); }

    @action
    setQnt(value: number) {
        if (value < 1) {
            value = 1;
        }
        else if (value > 500) {
            value = 500;
        }
        this._qnt = value;
    }

    @computed get Qnt(): number { return this._qnt; }

    //per computer per month: on annual that's the yearly charge / 12, on monthly it IS the rate
    @computed get PricePerMonthString(): string {
        const priceM = this.IsMonthly
            ? this.BundlePrice / this.Qnt
            : this.BundlePrice / 12 / this.Qnt;
        return priceM.toFixed(2);
    }

    @computed get PricePerComputerString(): string {
        return (this.BundlePrice / this.Qnt).toFixed(2);
    }

    //volume discount vs buying the same seats at the single-seat rate (same billing period)
    @computed get Saving(): number {
        return this.EffectivePriceList[1] * this.Qnt - this.BundlePrice;
    }

    @computed get SavingString(): string {
        return this.Saving.toFixed(2);
    }


    @computed get USDPrice(): number { return this.BundlePrice * this.rates["GBPUSD"]; }
    @computed get EURPrice(): number { return this.BundlePrice * this.rates["GBPEUR"]; }

    //computer property for the price in USD and EUR based on the current exchange rates as string with 2 decimal places
    //empty when rates aren't available (e.g. EU visitors are quoted in EUR directly, no FX line)
    @computed get USDPriceString(): string { return this.USDPrice ? this.USDPrice.toFixed(0) : ''; }
    @computed get EURPriceString(): string { return this.EURPrice ? this.EURPrice.toFixed(0) : ''; }

    //increase the quantity of the bundle by 1
    @action
    increaseQnt() {
        this.setQnt(this._qnt + 1);
    }

    //decrease the quantity of the bundle by 1
    @action
    decreaseQnt() {
        this.setQnt(this._qnt - 1);
    }




}